import Component from '@glimmer/component';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { concat } from '@ember/helper';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Chip from 'tio-ui/components/chip';
import { gte, notEq } from 'tio-ui/utilities';
import {
  PhTrophy,
  PhTrendUp,
  PhTrendDown,
  PhMinus,
  PhChartLine,
  PhLightning,
  PhTarget,
  PhPiggyBank,
  PhBook,
  PhUsers,
  PhMedal,
  PhCrown,
  PhStar,
  PhRocketLaunch,
  PhGraduationCap,
} from 'ember-phosphor-icons';
import { getCurrentDemoClientData } from 'tio-employee/utils/career-pathways-demo-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import StatCard from 'tio-ui/components/stat-card';
import SectionHeader from 'tio-ui/components/section-header';
import { formatMonthShort, formatShortDate } from 'tio-ui/format';
import BoostActionLink from 'tio-employee/components/reimagined/boost-action-link';
import type {
  CareerCapitalScore,
  ScoreHistoryPoint,
} from 'tio-employee/services/career-pathways-api';

interface ScoreModel {
  score: CareerCapitalScore;
  history: ScoreHistoryPoint[];
}

interface CareerCapitalScoreSignature {
  Args: {
    model: ScoreModel;
  };
}

export default class CareerCapitalScorePage extends Component<CareerCapitalScoreSignature> {
  @service declare reimaginedData: ReimaginedDataService;

  get demoClient() {
    return getCurrentDemoClientData();
  }

  get score() {
    return this.args.model.score;
  }

  get history() {
    return this.args.model.history;
  }

  get scoreChange30Days() {
    if (this.history.length < 2) return 0;
    const current = this.history[this.history.length - 1];
    const thirtyDaysAgo = this.history[Math.max(0, this.history.length - 4)];
    return current && thirtyDaysAgo ? current.score - thirtyDaysAgo.score : 0;
  }

  get levelConfig() {
    const configs = {
      bronze: {
        tierClass: 'tio-tier-bronze',
        icon: PhMedal,
        label: 'Bronze',
        minScore: 0,
        maxScore: 499,
      },
      silver: {
        tierClass: 'tio-tier-silver',
        icon: PhStar,
        label: 'Silver',
        minScore: 500,
        maxScore: 749,
      },
      gold: {
        tierClass: 'tio-tier-gold',
        icon: PhCrown,
        label: 'Gold',
        minScore: 750,
        maxScore: 899,
      },
      platinum: {
        tierClass: 'tio-tier-platinum',
        icon: PhTrophy,
        label: 'Platinum',
        minScore: 900,
        maxScore: 1000,
      },
    };
    return configs[this.score.level] || configs.bronze;
  }

  get scoreProgressPercent() {
    const config = this.levelConfig;
    const range = config.maxScore - config.minScore;
    const progress = this.score.totalScore - config.minScore;
    return Math.min(100, Math.max(0, (progress / range) * 100));
  }

  get scoreProgressStyle() {
    return htmlSafe(`width: ${this.scoreProgressPercent}%`);
  }

  get nextLevelProgress() {
    if (!this.score.nextMilestone) return 0;
    const targetScore = this.score.nextMilestone.targetScore;
    const currentLevelStart = this.levelConfig.minScore;
    const totalRange = targetScore - currentLevelStart;
    const currentProgress = this.score.totalScore - currentLevelStart;
    return Math.round((currentProgress / totalRange) * 100);
  }

  get nextLevelProgressStyle() {
    return htmlSafe(`width: ${this.nextLevelProgress}%`);
  }

  get componentsList() {
    const components = this.score.components;
    return [
      {
        key: 'skillVelocity',
        name: 'Skill Velocity',
        icon: PhLightning,
        metricBg: 'tio-metric-velocity',
        metricFg: 'tio-metric-velocity-fg',
        data: components.skillVelocity,
      },
      {
        key: 'careerReadiness',
        name: 'Career Readiness',
        icon: PhTarget,
        metricBg: 'tio-metric-readiness',
        metricFg: 'tio-metric-readiness-fg',
        data: components.careerReadiness,
      },
      {
        key: 'financialOptimization',
        name: 'Financial Optimization',
        icon: PhPiggyBank,
        metricBg: 'tio-metric-financial',
        metricFg: 'tio-metric-financial-fg',
        data: components.financialOptimization,
      },
      {
        key: 'learningProgress',
        name: 'Learning Progress',
        icon: PhBook,
        metricBg: 'tio-metric-learning',
        metricFg: 'tio-metric-learning-fg',
        data: components.learningProgress,
      },
      {
        key: 'networkDensity',
        name: 'Network Density',
        icon: PhUsers,
        metricBg: 'tio-metric-network',
        metricFg: 'tio-metric-network-fg',
        data: components.networkDensity,
      },
    ];
  }

  get recentBadges() {
    return this.score.badges.slice(0, 4);
  }

  get historyChartData() {
    const lastSix = this.history.slice(-6);
    const maxScore = Math.max(...lastSix.map((h) => h.score));
    const minScore = Math.min(...lastSix.map((h) => h.score));
    const range = maxScore - minScore || 1;

    return lastSix.map((point) => ({
      ...point,
      height: Math.round(((point.score - minScore) / range) * 100) + 20,
      formattedDate: formatMonthShort(new Date(point.date)),
    }));
  }

  getTrendIcon = (trend: string) => {
    if (trend === 'up') return PhTrendUp;
    if (trend === 'down') return PhTrendDown;
    return PhMinus;
  };

  getTrendColor = (trend: string) => {
    if (trend === 'up') return 'tio-fg-success';
    if (trend === 'down') return 'tio-fg-danger';
    return 'tio-fg-muted';
  };

  getScoreColor = (score: number) => {
    if (score >= 80) return 'tio-fg-success';
    if (score >= 60) return 'tio-fg-info';
    if (score >= 40) return 'tio-fg-warning';
    return 'tio-fg-danger';
  };

  getScoreBgColor = (score: number) => {
    if (score >= 80) return 'tio-bg-success';
    if (score >= 60) return 'tio-bg-info';
    if (score >= 40) return 'tio-bg-warning';
    return 'tio-bg-danger';
  };

  formatDate = (dateStr: string) => formatShortDate(new Date(dateStr));

  <template>
    {{! template-lint-disable no-bare-strings }}
    <DataIndicator @source="placeholder" @showBadge={{true}} @dataSourceId="career-capital-score">
      <VStack class="data-audit w-full min-w-0">
        {{! Hero Score Card — tier-aware gradient via .tio-tier-* + .tio-tier-hero }}
        <div
          class="tio-tier-hero rounded-xl p-6 tio-fg-inverse mb-6 relative overflow-hidden
            {{this.levelConfig.tierClass}}"
        >
          {{! Background decoration }}
          <div class="absolute top-0 right-0 opacity-10">
            <PhTrophy @size={{200}} />
          </div>

          <div class="relative z-10">
            <div class="tio-row mb-2">
              {{#let this.levelConfig.icon as |LevelIcon|}}
                <LevelIcon @size={{32}} />
              {{/let}}
              <Heading @level={{1}} class="tio-fg-inverse">Career Capital Score</Heading>
            </div>

            <div class="flex items-end gap-8 mt-4">
              <div>
                <div class="text-7xl font-bold">{{this.score.totalScore}}</div>
                <Text @size="lg" class="tio-growth-score-hero-muted">of 1,000 points</Text>
              </div>

              <div class="flex-1 max-w-md">
                <div class="tio-row-between mb-2">
                  <Chip class="tio-growth-score-level-chip">{{this.levelConfig.label}}
                    Level</Chip>
                  <Text class="tio-growth-score-hero-muted">Top {{this.score.percentile}}%</Text>
                </div>

                {{! Progress to next level }}
                <div class="tio-growth-score-next-track">
                  <div
                    class="h-full tio-bg-surface rounded-full transition-all"
                    style={{this.nextLevelProgressStyle}}
                  ></div>
                </div>

                {{#if this.score.nextMilestone}}
                  <div class="tio-growth-score-next-meta">
                    <span>{{this.score.nextMilestone.pointsNeeded}}
                      pts to
                      {{this.score.nextMilestone.level}}</span>
                    <span>Est. {{this.formatDate this.score.nextMilestone.estimatedDate}}</span>
                  </div>
                {{/if}}
              </div>
            </div>
          </div>
        </div>

        {{! Quick Stats Row }}
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            @icon={{PhChartLine}}
            @label="Company Rank"
            @value="#{{this.score.leaderboard.rank}}"
            @color="violet"
          >
            <:extra>
              <Text @variant="muted" @size="sm">of
                {{this.score.leaderboard.totalParticipants}}</Text>
            </:extra>
          </StatCard>

          <StatCard
            @icon={{PhUsers}}
            @label="Team Rank"
            @value="#{{this.score.leaderboard.teamRank}}"
            @color="blue"
          >
            <:extra>
              <Text @variant="muted" @size="sm">of {{this.score.leaderboard.teamSize}}</Text>
            </:extra>
          </StatCard>

          <StatCard
            @icon={{PhTrendUp}}
            @label="30-Day Change"
            @value="+{{this.scoreChange30Days}} points"
            @color="emerald"
            @valueClass="tio-fg-success"
          />

          <StatCard
            @icon={{PhMedal}}
            @label="Badges Earned"
            @value="{{this.score.badges.length}}"
            @subtext="achievements"
            @color="amber"
          />
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {{! Score Breakdown }}
          <div class="lg:col-span-2 tio-growth-score-card shadow-sm ring-1">
            <div class="tio-row-between mb-6">
              <Heading @level={{2}} @size="lg">Score Breakdown</Heading>
              <Text @variant="muted" @size="sm">5 components weighted by impact</Text>
            </div>

            <div class="space-y-5">
              {{#each this.componentsList as |comp|}}
                <div class="border-b tio-border-subtle pb-4 last:border-0 last:pb-0">
                  <div class="tio-row-between mb-2">
                    <div class="tio-row">
                      <div class="p-2 rounded-lg {{comp.metricBg}} {{comp.metricFg}}">
                        {{#let comp.icon as |Icon|}}
                          <Icon @size={{20}} />
                        {{/let}}
                      </div>
                      <div>
                        <Text @weight="medium">{{comp.name}}</Text>
                        <Text @variant="muted" @size="xs">{{comp.data.weight}}% weight</Text>
                      </div>
                    </div>

                    <div class="tio-row">
                      <div class="tio-row-extra-tight {{this.getTrendColor comp.data.trend}}">
                        {{#let (this.getTrendIcon comp.data.trend) as |TrendIcon|}}
                          <TrendIcon @size={{16}} />
                        {{/let}}
                      </div>
                      <div class="tio-text-2xl-bold {{this.getScoreColor comp.data.score}}">
                        {{comp.data.score}}
                      </div>
                    </div>
                  </div>

                  {{! Component progress bar }}
                  <div class="tio-growth-score-component-track">
                    <div
                      class="h-full rounded-full transition-all
                        {{this.getScoreBgColor comp.data.score}}"
                      style={{htmlSafe (concat "width: " comp.data.score "%")}}
                    ></div>
                  </div>

                  {{! Factors }}
                  <div class="tio-wrap-tight mt-3">
                    {{#each comp.data.factors as |factor|}}
                      <div
                        class="text-xs px-2 py-1 rounded
                          {{if
                            (gte factor.value factor.benchmark)
                            'tio-bg-success-soft tio-fg-success-body'
                            'tio-bg-warning-soft tio-fg-warning-body'
                          }}"
                      >
                        {{factor.name}}:
                        {{factor.value}}
                        {{#if (notEq factor.benchmark factor.value)}}
                          <span class="tio-fg-disabled">(target: {{factor.benchmark}})</span>
                        {{/if}}
                      </div>
                    {{/each}}
                  </div>
                </div>
              {{/each}}
            </div>
          </div>

          {{! Right Column }}
          <div class="space-y-6">
            {{! Recent Badges }}
            <div class="tio-growth-score-card shadow-sm ring-1">
              <div class="tio-row-between mb-4">
                <Heading @level={{3}}>Recent Badges</Heading>
                <Chip @intent="default">{{this.score.badges.length}} total</Chip>
              </div>

              <div class="space-y-3">
                {{#each this.recentBadges as |badge|}}
                  <div class="tio-row p-3 tio-surface-sunken">
                    <div class="p-2 tio-bg-warning-soft rounded-lg tio-fg-warning">
                      <PhTrophy @size={{24}} />
                    </div>
                    <div class="flex-1">
                      <Text @weight="medium">{{badge.name}}</Text>
                      <Text @variant="muted" @size="xs">{{badge.description}}</Text>
                    </div>
                    <Text @variant="muted" @size="xs">{{this.formatDate badge.earnedDate}}</Text>
                  </div>
                {{/each}}
              </div>
            </div>

            {{! Score History Mini Chart }}
            <div class="tio-growth-score-card shadow-sm ring-1">
              <Heading @level={{3}} class="mb-4">Score History</Heading>

              <div class="tio-flex-between items-end h-32 gap-2">
                {{#each this.historyChartData as |point|}}
                  <div class="flex-1 tio-stack-center">
                    <div
                      class="tio-growth-score-history-bar"
                      style={{htmlSafe (concat "height: " point.height "%")}}
                    ></div>
                    <Text @variant="muted" @size="xs" class="mt-2">{{point.formattedDate}}</Text>
                  </div>
                {{/each}}
              </div>
            </div>

            {{! Quick Actions }}
            <div class="tio-bg-brand-wash rounded-lg p-6 border tio-border-brand-soft">
              <SectionHeader
                @icon={{PhRocketLaunch}}
                @title="Boost Your Score"
                @iconClass="tio-fg-brand"
                @iconSize={{24}}
                @marginClass="mb-3"
              />

              <div class="space-y-2">
                <BoostActionLink
                  @route="authenticated.reimagined.skills"
                  @icon={{PhLightning}}
                  @label="Complete a skill assessment"
                  @points="+15 pts"
                  @iconClass="tio-fg-info"
                />

                <BoostActionLink
                  @route="authenticated.reimagined.recommendations"
                  @icon={{PhGraduationCap}}
                  @label="Start a recommended course"
                  @points="+25 pts"
                  @iconClass="tio-fg-brand"
                />

                <BoostActionLink
                  @route="authenticated.reimagined.finance"
                  @icon={{PhPiggyBank}}
                  @label="Optimize Section 127"
                  @points="+20 pts"
                  @iconClass="tio-fg-success"
                />
              </div>
            </div>
          </div>
        </div>
      </VStack>
    </DataIndicator>
  </template>
}
